import { SignInButton, SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { COLORADO, ORDER_VALIDATION_CODES, getColoradoPrivateResidenceDeliveryRejectReason } from "@the-drop/types";
import { Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { resolveClerkPublishableKey } from "../clerk-env";
import {
  GooglePlacesAutocompleteInput,
  type PickedAddress,
} from "../components/GooglePlacesAutocompleteInput";
import { PlaidLinkLaunchButton } from "../components/PlaidLinkLaunchButton";
import { resolveProductImageUrl } from "../lib/category-placeholder";
import {
  readCheckoutDeliveryAddress,
  writeCheckoutDeliveryAddress,
  type CheckoutDeliveryAddress,
} from "../lib/checkout-delivery-storage";
import {
  buildPlaceOrderPayload,
  resolveStraddlePaykeyFromEnv,
  submitPlaceOrder,
  SubmitOrderHttpError,
} from "../lib/checkout-order-submit";
import {
  readCheckoutDeliveryPaymentMethod,
  writeCheckoutDeliveryPaymentMethod,
} from "../lib/checkout-payment-preference";
import { readCheckoutShopMeta, writeCheckoutShopMeta } from "../lib/checkout-shop-meta";
import { coloradoPrivateResidenceRejectMessage } from "../lib/colorado-delivery-address-message";
import {
  createCustomerBankLinkToken,
  exchangeCustomerBankPublicToken,
  fetchCustomerBankProfile,
  formatBankLinkExchangeError,
  formatBankLinkStepError,
  type CustomerBankLinkProfile,
} from "../lib/customer-plaid-bank-client";
import { fetchCustomerDeliveryFeeCents } from "../lib/fetch-customer-delivery-fee";
import {
  AchAuthorizationPreviewError,
  fetchAchAuthorizationPreview,
  type AchAuthorizationPreview,
} from "../lib/fetch-ach-authorization-preview";
import { AchAuthorizationConsent } from "../components/AchAuthorizationConsent";
import {
  fetchCustomerSavedAddresses,
  type CustomerSavedAddress,
} from "../lib/fetch-customer-saved-addresses";
import { fetchDeliveryAddressAllowed } from "../lib/fetch-delivery-address-allowed";
import { fetchDeliveryZipPermitted } from "../lib/fetch-delivery-zip-permitted";
import { fetchDispensaryMenu } from "../lib/fetch-dispensary-menu";
import { fetchMe, formatMeFetchError } from "../lib/fetch-with-clerk";
import { formatCentsAsUsd } from "../lib/format-menu";
import { googlePlacesRejectMessage } from "../lib/google-places-reject-message";
import {
  cartSubtotalCents,
  formatReconcileSummary,
  lineSubtotalCents,
  useCartStore,
  type CartLine,
} from "../store/cart-store";

type Props = { dispensarySlug: string };

type ZipStatus =
  | { phase: "idle" }
  | { phase: "checking" }
  | { phase: "permitted"; zip: string }
  | { phase: "not_permitted"; zip: string }
  | { phase: "invalid_zip" }
  | { phase: "error"; message: string };

type PlaceCheck =
  | { phase: "idle" }
  | { phase: "checking"; placeId: string }
  | { phase: "allowed"; placeId: string; source: "google_places" | "fallback_regex" | "no_place_id"; warning?: string }
  | {
      phase: "rejected";
      placeId: string;
      source: "google_places" | "fallback_regex";
      reasonMessage: string;
      reasonCode: string;
    };

type PaymentCardState = "unselected" | "ready" | "needs_action" | "error";

function formatSubmitError(body: unknown): string {
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    if (o.code === ORDER_VALIDATION_CODES.DELIVERY_ADDRESS_NOT_ELIGIBLE && typeof o.reason === "string") {
      return coloradoPrivateResidenceRejectMessage(o.reason);
    }
    if (o.code === ORDER_VALIDATION_CODES.ORDER_TOTAL_MISMATCH) {
      return "Order total does not match current pricing. Refresh this page and try again.";
    }
    if (typeof o.code === "string") return o.code;
    if (o.error === "customer_straddle_paykey_required") {
      return "No payment method on file. Link a bank in the Payment section, or switch to cash at delivery.";
    }
    const msg = typeof o.message === "string" && o.message.trim().length > 0 ? o.message : null;
    if (typeof o.error === "string") return msg ?? o.error;
    if (msg) return msg;
  }
  return "Order could not be placed.";
}

export function CheckoutScreen({ dispensarySlug }: Props) {
  const clerkPk = resolveClerkPublishableKey();
  if (!clerkPk) {
    return <CheckoutWithoutClerk dispensarySlug={dispensarySlug} />;
  }
  return <CheckoutWithClerk dispensarySlug={dispensarySlug} />;
}

function CheckoutWithoutClerk({ dispensarySlug }: Props) {
  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6">
      <Header />
      <div className="rounded-2xl border border-[var(--card-border)] bg-white p-6 shadow-cardMd">
        <p className="text-sm text-amber-900">
          Set <span className="font-mono">VITE_CLERK_PUBLISHABLE_KEY</span> to sign in and place an order.
        </p>
        <Link
          to="/$dispensarySlug/cart"
          params={{ dispensarySlug }}
          className="mt-3 inline-block text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--green)]"
        >
          ← Back to cart
        </Link>
      </div>
    </div>
  );
}

function CheckoutWithClerk({ dispensarySlug }: Props) {
  const navigate = useNavigate();
  const lines = useCartStore((s) => s.carts[dispensarySlug] ?? []);
  const clearCart = useCartStore((s) => s.clearCart);
  const { getToken, isLoaded, isSignedIn } = useAuth();

  const addressSectionRef = useRef<HTMLElement | null>(null);
  const paymentSectionRef = useRef<HTMLElement | null>(null);

  /* ─── Address state ───────────────────────────────────────────── */
  const savedAddress = useMemo(() => readCheckoutDeliveryAddress(dispensarySlug), [dispensarySlug]);
  const [line1, setLine1] = useState(savedAddress?.line1 ?? "");
  const [line2, setLine2] = useState(savedAddress?.line2 ?? "");
  const [city, setCity] = useState(savedAddress?.city ?? "");
  const [zipInput, setZipInput] = useState(savedAddress?.zip ?? "");
  const [zipStatus, setZipStatus] = useState<ZipStatus>(() =>
    savedAddress?.zip ? { phase: "permitted", zip: savedAddress.zip } : { phase: "idle" },
  );
  const [placeId, setPlaceId] = useState<string | null>(savedAddress?.placeId ?? null);
  const [placeCheck, setPlaceCheck] = useState<PlaceCheck>({ phase: "idle" });

  const residenceRejectReason = useMemo(
    () =>
      getColoradoPrivateResidenceDeliveryRejectReason({
        deliveryState: "CO",
        deliveryAddressLine1: line1,
        deliveryAddressLine2: line2,
      }),
    [line1, line2],
  );

  /* ─── Payment state ───────────────────────────────────────────── */
  const [payCashAtDelivery, setPayCashAtDelivery] = useState(
    () => readCheckoutDeliveryPaymentMethod(dispensarySlug) === "cash_at_delivery",
  );
  const [linkedProfile, setLinkedProfile] = useState<CustomerBankLinkProfile | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    writeCheckoutDeliveryPaymentMethod(dispensarySlug, payCashAtDelivery ? "cash_at_delivery" : "ach");
  }, [dispensarySlug, payCashAtDelivery]);

  /* ─── Submit / fees state ─────────────────────────────────────── */
  const [dispensaryId, setDispensaryId] = useState<string | null>(
    () => readCheckoutShopMeta(dispensarySlug)?.dispensaryId ?? null,
  );
  const [shopLoadError, setShopLoadError] = useState<string | null>(null);
  const [customerDeliveryFeeCents, setCustomerDeliveryFeeCents] = useState<number | null>(null);
  const [feeLoadError, setFeeLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reconcileNotice, setReconcileNotice] = useState<string | null>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  /* ─── Effects: ZIP / Place verification (preserved verbatim) ──── */
  const runZipCheck = useCallback(async (): Promise<string | null> => {
    setZipStatus({ phase: "checking" });
    const r = await fetchDeliveryZipPermitted(zipInput);
    if (r.ok) {
      setZipStatus({ phase: "permitted", zip: r.zip });
      return r.zip;
    }
    switch (r.kind) {
      case "not_permitted":
        setZipStatus({ phase: "not_permitted", zip: r.zip });
        break;
      case "invalid_zip":
        setZipStatus({ phase: "invalid_zip" });
        break;
      case "network":
        setZipStatus({ phase: "error", message: "Could not reach the server. Try again." });
        break;
      case "bad_request":
        setZipStatus({ phase: "error", message: "Enter a ZIP code to check delivery area." });
        break;
      case "http":
        setZipStatus({ phase: "error", message: `Delivery check failed (${r.status}).` });
        break;
    }
    return null;
  }, [zipInput]);

  const applySavedAddress = useCallback((a: CustomerSavedAddress) => {
    setLine1(a.addressLine1);
    setLine2(a.addressLine2 ?? "");
    setCity(a.city);
    setZipInput(a.zip);
    setZipStatus({ phase: "permitted", zip: a.zip });
    setPlaceId(null);
    setPlaceCheck({ phase: "checking", placeId: a.id });
    void (async () => {
      const r = await fetchDeliveryAddressAllowed({
        deliveryAddressLine1: a.addressLine1,
        deliveryAddressLine2:
          a.addressLine2 != null && a.addressLine2.trim().length > 0 ? a.addressLine2 : undefined,
        deliveryState: "CO",
        deliveryZip: a.zip,
      });
      if (r.ok) {
        setPlaceCheck({ phase: "allowed", placeId: a.id, source: r.source, warning: r.warning });
        return;
      }
      if (r.source === "fallback_regex") {
        setPlaceCheck({
          phase: "rejected",
          placeId: a.id,
          source: "fallback_regex",
          reasonCode: r.reason,
          reasonMessage: coloradoPrivateResidenceRejectMessage(r.reason),
        });
        return;
      }
      if (r.source === "zip_jurisdiction") {
        if (r.reason === "invalid_zip") setZipStatus({ phase: "invalid_zip" });
        else setZipStatus({ phase: "not_permitted", zip: r.zip ?? a.zip });
        setPlaceCheck({ phase: "idle" });
        return;
      }
      setPlaceCheck({
        phase: "allowed",
        placeId: a.id,
        source: "fallback_regex",
        warning: "We couldn't reach the address checker. Delivery will be confirmed by the driver at the door.",
      });
    })();
  }, []);

  const onAutocompletePick = useCallback((p: PickedAddress) => {
    setLine1(p.line1);
    if (p.city) setCity(p.city);
    if (p.zip) {
      setZipInput(p.zip);
      setZipStatus({ phase: "idle" });
    }
    setPlaceId(p.placeId);
  }, []);

  useEffect(() => {
    if (!placeId) {
      setPlaceCheck({ phase: "idle" });
      return;
    }
    let cancelled = false;
    setPlaceCheck({ phase: "checking", placeId });
    void (async () => {
      const currentZip = zipInput.trim();
      const r = await fetchDeliveryAddressAllowed({
        placeId,
        deliveryAddressLine1: line1,
        deliveryAddressLine2: line2.trim().length > 0 ? line2.trim() : undefined,
        deliveryState: "CO",
        deliveryZip: currentZip.length >= 5 ? currentZip : undefined,
      });
      if (cancelled) return;
      if (r.ok) {
        setPlaceCheck({ phase: "allowed", placeId, source: r.source, warning: r.warning });
        if (currentZip.length >= 5) setZipStatus({ phase: "permitted", zip: currentZip });
        return;
      }
      if (r.source === "network" || r.source === "http") {
        setPlaceCheck({
          phase: "allowed",
          placeId,
          source: "fallback_regex",
          warning:
            "We couldn't reach the address checker. Delivery will be confirmed by the driver at the door.",
        });
        return;
      }
      if (r.source === "zip_jurisdiction") {
        if (r.reason === "invalid_zip") setZipStatus({ phase: "invalid_zip" });
        else setZipStatus({ phase: "not_permitted", zip: r.zip ?? currentZip });
        setPlaceCheck({ phase: "idle" });
        return;
      }
      if (r.source === "google_places") {
        setPlaceCheck({
          phase: "rejected",
          placeId,
          source: "google_places",
          reasonCode: r.reason,
          reasonMessage: googlePlacesRejectMessage(r.reason),
        });
        return;
      }
      if (r.source === "fallback_regex") {
        setPlaceCheck({
          phase: "rejected",
          placeId,
          source: "fallback_regex",
          reasonCode: r.reason,
          reasonMessage: coloradoPrivateResidenceRejectMessage(r.reason),
        });
        return;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  const placeRejected = placeCheck.phase === "rejected";
  const zipRejected = zipStatus.phase === "not_permitted" || zipStatus.phase === "invalid_zip";

  const addressFieldsReady =
    line1.trim().length > 0 &&
    city.trim().length > 0 &&
    zipInput.trim().length >= 5 &&
    residenceRejectReason == null &&
    !placeRejected &&
    !zipRejected &&
    placeCheck.phase === "allowed";

  const showPickFromDropdownHint =
    line1.trim().length >= 5 &&
    placeCheck.phase === "idle" &&
    residenceRejectReason == null &&
    !zipRejected;

  const paymentReady = payCashAtDelivery || (linkedProfile?.secrets.plaidItemConfigured ?? false);

  /* ─── Effects: Delivery fee + dispensary id (lifted from review) ─ */
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    setFeeLoadError(null);
    void (async () => {
      try {
        const cents = await fetchCustomerDeliveryFeeCents(getToken);
        if (!cancelled) setCustomerDeliveryFeeCents(cents);
      } catch {
        if (!cancelled) {
          setCustomerDeliveryFeeCents(0);
          setFeeLoadError("Could not load delivery fee; using $0.00 for this session.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    if (dispensaryId) return;
    let cancelled = false;
    void (async () => {
      try {
        const menu = await fetchDispensaryMenu(dispensarySlug);
        if (cancelled) return;
        writeCheckoutShopMeta(dispensarySlug, {
          dispensaryId: menu.dispensary.id,
          publicSlug: menu.dispensary.publicSlug,
        });
        setDispensaryId(menu.dispensary.id);
      } catch {
        if (!cancelled) {
          setShopLoadError(
            "Could not resolve dispensary id (menu fetch failed). Return to the menu and try again.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispensarySlug, dispensaryId]);

  /* ─── Submit ──────────────────────────────────────────────────── */
  const subtotal = useMemo(() => cartSubtotalCents(lines), [lines]);
  const coSurchargeCents = COLORADO.PURCHASE_LIMITS.CO_DELIVERY_SURCHARGE_CENTS;
  const orderTotalCents =
    customerDeliveryFeeCents == null ? null : subtotal + customerDeliveryFeeCents + coSurchargeCents;
  const envPaykeyOverride = resolveStraddlePaykeyFromEnv();
  const deliveryPaymentMethod = useMemo(
    () => readCheckoutDeliveryPaymentMethod(dispensarySlug),
    [dispensarySlug],
  );

  /* ─── ACH authorization (Nacha WEB) ───────────────────────────── */
  const isBankPayment = deliveryPaymentMethod !== "cash_at_delivery";
  const [achPreview, setAchPreview] = useState<AchAuthorizationPreview | null>(null);
  const [achLoading, setAchLoading] = useState(false);
  const [achError, setAchError] = useState<string | null>(null);
  const [achAccepted, setAchAccepted] = useState(false);
  const [achReloadNonce, setAchReloadNonce] = useState(0);

  useEffect(() => {
    // Cash orders originate no ACH entry, so there is nothing to authorize.
    if (!isBankPayment || !isSignedIn || !dispensaryId || lines.length === 0) {
      setAchPreview(null);
      setAchAccepted(false);
      return;
    }
    let cancelled = false;
    setAchLoading(true);
    setAchError(null);
    (async () => {
      try {
        const preview = await fetchAchAuthorizationPreview(getToken, {
          dispensaryId,
          subtotalCents: subtotal,
          taxCents: 0,
          coSurchargeCents,
        });
        if (cancelled) return;
        setAchPreview(preview);
        // Any change to the amounts invalidates prior assent — the consumer
        // agreed to a specific number, not to "whatever the total becomes".
        setAchAccepted(false);
      } catch (e) {
        if (cancelled) return;
        setAchPreview(null);
        setAchAccepted(false);
        setAchError(e instanceof AchAuthorizationPreviewError ? e.code : "preview_failed");
      } finally {
        if (!cancelled) setAchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isBankPayment,
    isSignedIn,
    dispensaryId,
    lines.length,
    subtotal,
    coSurchargeCents,
    getToken,
    achReloadNonce,
  ]);

  function scrollToAddress() {
    addressSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  function scrollToPayment() {
    paymentSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const onSubmit = useCallback(async () => {
    if (zipStatus.phase === "checking") return;

    if (!addressFieldsReady) {
      scrollToAddress();
      return;
    }
    const currentZip = zipInput.trim();
    const verifiedZip =
      zipStatus.phase === "permitted" && zipStatus.zip === currentZip
        ? zipStatus.zip
        : await runZipCheck();
    if (!verifiedZip) {
      scrollToAddress();
      return;
    }
    if (!paymentReady) {
      scrollToPayment();
      return;
    }
    if (!isSignedIn || !dispensaryId || lines.length === 0 || customerDeliveryFeeCents == null) {
      return;
    }

    const addressPayload: CheckoutDeliveryAddress = {
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: "CO",
      zip: verifiedZip,
    };
    if (placeId && placeCheck.phase === "allowed" && placeCheck.placeId === placeId) {
      addressPayload.placeId = placeId;
    }
    writeCheckoutDeliveryAddress(dispensarySlug, addressPayload);

    setSubmitError(null);
    setReconcileNotice(null);
    setSubmitting(true);

    try {
      try {
        const fresh = await fetchDispensaryMenu(dispensarySlug);
        const summary = useCartStore.getState().reconcileCartWithMenu(dispensarySlug, fresh.items);
        const msg = formatReconcileSummary(summary);
        if (msg) {
          setReconcileNotice(msg);
          idempotencyKeyRef.current = crypto.randomUUID();
          setSubmitting(false);
          return;
        }
      } catch {
        // API down — let the server enforce inventory.
      }

      const currentLines = useCartStore.getState().carts[dispensarySlug] ?? [];
      if (currentLines.length === 0) {
        setSubmitError("Your cart is empty after a stock update. Return to the menu to add items.");
        setSubmitting(false);
        return;
      }

      // Belt and braces behind the disabled submit button: never place a bank
      // order without assent, even if the button guard were bypassed.
      if (isBankPayment && (achPreview == null || !achAccepted)) {
        setSubmitError("Please authorize the bank payment to place your order.");
        setSubmitting(false);
        return;
      }

      const body = buildPlaceOrderPayload({
        dispensaryId,
        address: addressPayload,
        lines: currentLines,
        customerDeliveryFeeCents,
        deliveryPaymentMethod,
        ...(envPaykeyOverride ? { straddlePaykey: envPaykeyOverride } : {}),
        ...(isBankPayment && achPreview != null && achAccepted
          ? {
              achAuthorization: {
                accepted: true as const,
                // The hash of the text actually displayed. The server
                // re-renders and rejects a mismatch, so a fee change between
                // preview and submit surfaces as a re-consent rather than a
                // silent debit of a different amount.
                acceptedTextSha256: achPreview.authorizationTextSha256,
                authorizationVersion: achPreview.authorizationVersion,
              },
            }
          : {}),
      });
      const result = await submitPlaceOrder(getToken, idempotencyKeyRef.current, body);
      clearCart(dispensarySlug);
      await navigate({
        to: "/$dispensarySlug/orders/$orderId",
        params: { dispensarySlug, orderId: result.id },
      });
    } catch (e) {
      if (e instanceof SubmitOrderHttpError) setSubmitError(formatSubmitError(e.body));
      else setSubmitError("Order could not be placed.");
    } finally {
      setSubmitting(false);
    }
  }, [
    addressFieldsReady,
    city,
    clearCart,
    customerDeliveryFeeCents,
    deliveryPaymentMethod,
    dispensaryId,
    dispensarySlug,
    envPaykeyOverride,
    getToken,
    isSignedIn,
    line1,
    line2,
    lines.length,
    navigate,
    paymentReady,
    placeCheck,
    placeId,
    runZipCheck,
    zipInput,
    zipStatus,
    isBankPayment,
    achPreview,
    achAccepted,
  ]);

  const submitButtonDisabled =
    submitting ||
    !isSignedIn ||
    !dispensaryId ||
    lines.length === 0 ||
    customerDeliveryFeeCents == null ||
    !!shopLoadError ||
    // Nacha: a bank order cannot be placed until the consumer has affirmatively
    // authorized the debit. Assent must be an act, so the default is blocked.
    (isBankPayment && (achPreview == null || !achAccepted));

  return (
    /**
     * `<fieldset disabled={submitting}>` is the cheapest "freeze the form on
     * submit" pattern — native HTML disables every nested input and button,
     * AT announces the change, no per-field plumbing. UA-reset classes per
     * Q7 sign-off so Firefox doesn't paint a default fieldset border.
     */
    <fieldset
      disabled={submitting}
      className="m-0 min-w-0 border-0 p-0"
    >
      <div className="mx-auto w-full max-w-[1200px] space-y-6 pt-6 pb-32 md:pb-16">
        <Header />

        {/* Mobile collapsed-summary header (A4 override). md:hidden so it
         *  disappears on tablet+ where the right-column panel takes over. */}
        <MobileSummaryHeader
          lines={lines}
          subtotal={subtotal}
          customerDeliveryFeeCents={customerDeliveryFeeCents}
          orderTotalCents={orderTotalCents}
          coSurchargeCents={coSurchargeCents}
          dispensarySlug={dispensarySlug}
        />

        {/* aria-live region — desktop spinner is far from the Payment section
         *  on the left; this carries the announcement so SR users hear it. */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {submitting ? "Placing order…" : null}
          {submitError ? `Order error: ${submitError}` : null}
        </div>

        <div className="grid gap-6 md:grid-cols-3 md:gap-8">
          <div className="space-y-6 md:col-span-2">
            <SavedAddressChips onApply={applySavedAddress} />

            <DeliveryAddressGroup
              ref={addressSectionRef}
              line1={line1}
              line2={line2}
              city={city}
              zipInput={zipInput}
              zipStatus={zipStatus}
              placeCheck={placeCheck}
              placeId={placeId}
              residenceRejectReason={residenceRejectReason}
              showPickFromDropdownHint={showPickFromDropdownHint}
              setLine1={setLine1}
              setLine2={setLine2}
              setCity={setCity}
              setZipInput={setZipInput}
              setZipStatus={setZipStatus}
              setPlaceId={setPlaceId}
              setPlaceCheck={setPlaceCheck}
              onAutocompletePick={onAutocompletePick}
            />

            <PaymentSection
              ref={paymentSectionRef}
              addressFieldsReady={addressFieldsReady}
              payCashAtDelivery={payCashAtDelivery}
              setPayCashAtDelivery={setPayCashAtDelivery}
              linkedProfile={linkedProfile}
              setLinkedProfile={setLinkedProfile}
              paymentError={paymentError}
              setPaymentError={setPaymentError}
            />

            {/* Nacha WEB authorization. Sits directly above the submit control
             *  so the consumer reads what they are authorizing immediately
             *  before placing the order — not buried elsewhere on the page.
             *  Cash pays the driver at the door and originates no ACH entry,
             *  so no authorization is shown or required. */}
            {isBankPayment && isSignedIn && addressFieldsReady && lines.length > 0 ? (
              <AchAuthorizationConsent
                preview={achPreview}
                accepted={achAccepted}
                onAcceptedChange={setAchAccepted}
                loading={achLoading}
                error={achError}
                onRetry={() => setAchReloadNonce((n) => n + 1)}
                disabled={submitting}
              />
            ) : null}

            {reconcileNotice ? (
              <div
                role="status"
                className="rounded-2xl border border-[var(--green-pale)] bg-[var(--surface-sage)] p-4 text-sm text-[var(--green-dark)]"
              >
                <p className="font-semibold">Cart updated to match available inventory</p>
                <p className="mt-1">{reconcileNotice}</p>
                <p className="mt-2 text-xs">Review the updated totals, then place your order again.</p>
              </div>
            ) : null}

            {submitError ? (
              <div
                role="alert"
                className="rounded-2xl border border-[var(--badge-red)] bg-[var(--status-error-bg)] p-4 text-sm text-[var(--status-error-text)]"
              >
                {submitError}
              </div>
            ) : null}

            {shopLoadError ? (
              <p className="text-sm text-[var(--badge-red)]">{shopLoadError}</p>
            ) : null}
            {feeLoadError ? <p className="text-xs text-amber-900">{feeLoadError}</p> : null}
          </div>

          <OrderSummaryPanel
            lines={lines}
            subtotal={subtotal}
            customerDeliveryFeeCents={customerDeliveryFeeCents}
            coSurchargeCents={coSurchargeCents}
            orderTotalCents={orderTotalCents}
            taxCents={0}
            deliveryPaymentMethod={deliveryPaymentMethod}
            submitting={submitting}
            disabled={submitButtonDisabled}
            onSubmit={() => void onSubmit()}
          />
        </div>

        <MobileStickyBar
          orderTotalCents={orderTotalCents}
          submitting={submitting}
          disabled={submitButtonDisabled}
          onSubmit={() => void onSubmit()}
        />
      </div>
    </fieldset>
  );
}

/* ─── Subcomponents ─────────────────────────────────────────────── */

function Header() {
  return (
    <header className="space-y-2 border-b border-[var(--card-border)] pb-6">
      <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--green)]">
        Checkout
      </p>
      <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">
        Complete your order
      </h1>
    </header>
  );
}

function SavedAddressChips({ onApply }: { onApply: (a: CustomerSavedAddress) => void }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [addresses, setAddresses] = useState<CustomerSavedAddress[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;
    setLoadError(null);
    void (async () => {
      try {
        const { addresses: list } = await fetchCustomerSavedAddresses(getToken);
        if (!cancelled) setAddresses(list);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof Error && /^saved_addresses_http_403$/.test(e.message)) return;
        if (e instanceof Error && /^saved_addresses_http_(\d+)$/.test(e.message)) {
          const code = Number(/^saved_addresses_http_(\d+)$/.exec(e.message)?.[1]);
          setLoadError(
            code === 404
              ? "Saved addresses unavailable (API 404). Restart the API and apply migration 013."
              : `Saved addresses unavailable (HTTP ${code}).`,
          );
          return;
        }
        setLoadError("Could not load saved addresses.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded || !isSignedIn) return null;
  if (addresses.length === 0 && !loadError) return null;

  return (
    <div className="space-y-2">
      <SectionLabel>Saved addresses</SectionLabel>
      <div role="list" className="flex flex-wrap gap-2">
        {addresses.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onApply(a)}
            className="rounded-full border border-[var(--card-border)] bg-white px-3 py-1.5 text-sm text-[var(--text-primary)] transition-colors hover:border-[var(--green)] hover:text-[var(--green)]"
          >
            <span className="font-semibold">{a.label || "Saved"}</span>
            <span className="text-[var(--text-muted)]"> · {a.zip}</span>
          </button>
        ))}
      </div>
      {loadError ? <p className="text-xs text-amber-900">{loadError}</p> : null}
      <Link
        to="/account/addresses"
        className="inline-block text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--green)]"
      >
        Manage saved addresses →
      </Link>
    </div>
  );
}

type DeliveryAddressGroupProps = {
  line1: string;
  line2: string;
  city: string;
  zipInput: string;
  zipStatus: ZipStatus;
  placeCheck: PlaceCheck;
  placeId: string | null;
  residenceRejectReason: ReturnType<typeof getColoradoPrivateResidenceDeliveryRejectReason>;
  showPickFromDropdownHint: boolean;
  setLine1: (v: string) => void;
  setLine2: (v: string) => void;
  setCity: (v: string) => void;
  setZipInput: (v: string) => void;
  setZipStatus: (v: ZipStatus) => void;
  setPlaceId: (v: string | null) => void;
  setPlaceCheck: (v: PlaceCheck) => void;
  onAutocompletePick: (p: PickedAddress) => void;
};

const DeliveryAddressGroup = (() => {
  function DeliveryAddressGroupInner(
    props: DeliveryAddressGroupProps,
    ref: React.Ref<HTMLElement>,
  ) {
    const {
      line1, line2, city, zipInput, zipStatus, placeCheck, placeId, residenceRejectReason,
      showPickFromDropdownHint,
      setLine1, setLine2, setCity, setZipInput, setZipStatus, setPlaceId, setPlaceCheck, onAutocompletePick,
    } = props;

    const inputCls =
      "w-full border-0 border-b border-[var(--card-border)] bg-transparent px-0 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] focus:border-[var(--green)] focus:outline-none";

    return (
      <section ref={ref} className="scroll-mt-20 space-y-3">
        <SectionLabel>Delivery address</SectionLabel>
        <p className="text-xs text-[var(--text-muted)]">
          Colorado rules require a private residence in-state — not PO boxes, schools, or mailbox stores.
        </p>

        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-4 shadow-cardMd">
          <div className="space-y-3">
            <FieldLabel label="Street address">
              <GooglePlacesAutocompleteInput
                value={line1}
                onTextChange={(v) => {
                  setLine1(v);
                  if (placeId) setPlaceId(null);
                  if (placeCheck.phase !== "idle") setPlaceCheck({ phase: "idle" });
                }}
                onPick={onAutocompletePick}
                placeholder="123 Main St"
                className={inputCls}
                autoComplete="street-address"
              />
            </FieldLabel>
            <FieldLabel label="Apt / suite (optional)">
              <input
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                autoComplete="address-line2"
                className={inputCls}
              />
            </FieldLabel>
            <FieldLabel label="City">
              <input
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  if (placeId) setPlaceId(null);
                  if (placeCheck.phase !== "idle") setPlaceCheck({ phase: "idle" });
                }}
                autoComplete="address-level2"
                className={inputCls}
              />
            </FieldLabel>
            <div className="grid grid-cols-3 gap-3">
              <FieldLabel label="State">
                <input value="CO" readOnly className={`${inputCls} text-[var(--text-muted)]`} />
              </FieldLabel>
              <FieldLabel label="ZIP" className="col-span-2">
                <input
                  value={zipInput}
                  onChange={(e) => {
                    setZipInput(e.target.value);
                    setZipStatus({ phase: "idle" });
                    if (placeId) setPlaceId(null);
                    if (placeCheck.phase !== "idle") setPlaceCheck({ phase: "idle" });
                  }}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="80202"
                  className={inputCls}
                />
              </FieldLabel>
            </div>
          </div>
        </div>

        <p className="text-xs text-[var(--text-muted)]">
          Start typing and pick from the dropdown — we verify it's a private residence before you can continue.
        </p>

        {/* Status pills (compliance messages). Restyled from boxed banners
         *  to small inline pills matching marketplace cart-tip pattern. */}
        {residenceRejectReason ? (
          <StatusPill kind="error">
            {coloradoPrivateResidenceRejectMessage(residenceRejectReason)}
          </StatusPill>
        ) : null}
        {zipStatus.phase === "permitted" ? (
          <StatusPill kind="ok">Delivery available to ZIP {zipStatus.zip}</StatusPill>
        ) : null}
        {zipStatus.phase === "not_permitted" ? (
          <StatusPill kind="warn">We don't deliver to ZIP {zipStatus.zip} yet</StatusPill>
        ) : null}
        {zipStatus.phase === "invalid_zip" ? (
          <StatusPill kind="error">Enter a valid Colorado ZIP code (5 digits)</StatusPill>
        ) : null}
        {zipStatus.phase === "error" ? (
          <StatusPill kind="error">{zipStatus.message}</StatusPill>
        ) : null}
        {zipStatus.phase === "checking" ? (
          <StatusPill kind="info">Checking delivery area…</StatusPill>
        ) : null}
        {showPickFromDropdownHint ? (
          <StatusPill kind="warn">
            Pick your address from the dropdown so we can verify it's a private residence.
          </StatusPill>
        ) : null}
        {placeCheck.phase === "checking" ? (
          <StatusPill kind="info">Verifying with Google…</StatusPill>
        ) : null}
        {placeCheck.phase === "allowed" && placeCheck.source === "google_places" ? (
          <StatusPill kind="ok">Verified private residence</StatusPill>
        ) : null}
        {placeCheck.phase === "allowed" && placeCheck.warning ? (
          <StatusPill kind="warn">{placeCheck.warning}</StatusPill>
        ) : null}
        {placeCheck.phase === "rejected" ? (
          <StatusPill kind="error">{placeCheck.reasonMessage}</StatusPill>
        ) : null}
      </section>
    );
  }
  return Object.assign(
    // eslint-disable-next-line react/display-name
    React.forwardRef(DeliveryAddressGroupInner),
    { displayName: "DeliveryAddressGroup" },
  );
})();

type PaymentSectionProps = {
  addressFieldsReady: boolean;
  payCashAtDelivery: boolean;
  setPayCashAtDelivery: (on: boolean) => void;
  linkedProfile: CustomerBankLinkProfile | null;
  setLinkedProfile: (p: CustomerBankLinkProfile | null) => void;
  paymentError: string | null;
  setPaymentError: (m: string | null) => void;
};

const PaymentSection = (() => {
  function PaymentSectionInner(props: PaymentSectionProps, ref: React.Ref<HTMLElement>) {
    const {
      addressFieldsReady,
      payCashAtDelivery,
      setPayCashAtDelivery,
      linkedProfile,
      setLinkedProfile,
      paymentError,
      setPaymentError,
    } = props;

    const { getToken, isLoaded, isSignedIn } = useAuth();
    const [meError, setMeError] = useState<string | null>(null);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [linkToken, setLinkToken] = useState<string | null>(null);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [paymentLoading, setPaymentLoading] = useState(false);

    useEffect(() => {
      if (!isLoaded || !isSignedIn) return;
      let cancelled = false;
      void (async () => {
        try {
          const me = await fetchMe(getToken);
          if (cancelled) return;
          if (!me.user.roles.includes("customer") || !me.user.customerId) {
            setMeError(
              !me.user.roles.includes("customer")
                ? `Signed-in account does not include the customer role (primary role "${me.user.role}").`
                : "Your account has no customer profile row yet. Hard-refresh and try again.",
            );
            return;
          }
          setCustomerId(me.user.customerId);
        } catch (e) {
          if (!cancelled) setMeError(formatMeFetchError(e));
        }
      })();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, isSignedIn]);

    useEffect(() => {
      if (payCashAtDelivery) setPaymentLoading(false);
    }, [payCashAtDelivery]);

    useEffect(() => {
      if (!customerId || !isSignedIn || payCashAtDelivery) return;
      let cancelled = false;
      setPaymentLoading(true);
      setTokenError(null);
      void (async () => {
        try {
          const profile = await fetchCustomerBankProfile(getToken, customerId);
          if (cancelled) return;
          if (profile.secrets.plaidItemConfigured) {
            setLinkedProfile(profile);
            setLinkToken(null);
            return;
          }
          const { linkToken: tok } = await createCustomerBankLinkToken(getToken, customerId);
          if (!cancelled) setLinkToken(tok);
        } catch (e) {
          if (!cancelled) setTokenError(formatBankLinkStepError(e));
        } finally {
          if (!cancelled) setPaymentLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, isSignedIn, payCashAtDelivery]);

    const onPlaidSuccess = useCallback(
      async (publicToken: string, metadata: { accounts?: { id: string }[] }) => {
        if (!customerId) return;
        setPaymentError(null);
        try {
          const accountId = metadata.accounts?.[0]?.id;
          const profile = await exchangeCustomerBankPublicToken(getToken, customerId, publicToken, accountId);
          setLinkedProfile(profile);
          setLinkToken(null);
        } catch (e) {
          setPaymentError(formatBankLinkExchangeError(e));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [customerId],
    );

    const cashCardState: PaymentCardState = payCashAtDelivery ? "ready" : "unselected";

    let bankCardState: PaymentCardState;
    if (paymentError) bankCardState = "error";
    else if (payCashAtDelivery) bankCardState = "unselected";
    else if (linkedProfile?.secrets.plaidItemConfigured) bankCardState = "ready";
    else bankCardState = "needs_action";

    return (
      <section
        ref={ref}
        aria-disabled={!addressFieldsReady}
        className={`scroll-mt-20 space-y-3 transition-opacity ${
          addressFieldsReady ? "opacity-100" : "pointer-events-none opacity-50"
        }`}
      >
        <SectionLabel>Payment</SectionLabel>
        <p className="text-xs text-[var(--text-muted)]">
          {addressFieldsReady
            ? "Pay cash at delivery, or link a bank account."
            : "Finish the address above to choose a payment method."}
        </p>

        <SignedOut>
          <div className="rounded-2xl border border-[var(--card-border)] bg-white p-4 shadow-cardMd">
            <p className="text-sm text-[var(--text-secondary)]">Sign in to pay or link a bank account.</p>
            <SignInButton mode="modal">
              <button
                type="button"
                className="mt-3 inline-flex items-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
              >
                Sign in
              </button>
            </SignInButton>
          </div>
        </SignedOut>

        <SignedIn>
          {meError ? <StatusPill kind="error">{meError}</StatusPill> : null}

          <PaymentCard
            state={cashCardState}
            title="Cash at delivery"
            description="Have exact change ready for the driver — total includes the Colorado delivery surcharge."
            onSelect={() => {
              setPayCashAtDelivery(true);
              setPaymentError(null);
            }}
          />

          <PaymentCard
            state={bankCardState}
            title="Bank account (Plaid)"
            description="Securely link a bank with Plaid. ACH on order placement."
            onSelect={() => {
              setPayCashAtDelivery(false);
              setPaymentError(null);
            }}
          >
            {bankCardState === "ready" && linkedProfile ? (
              <div className="space-y-1 text-sm text-[var(--text-primary)]">
                {linkedProfile.bankName ? (
                  <p className="font-semibold">{linkedProfile.bankName}</p>
                ) : null}
                {linkedProfile.bankAccountLast4 ? (
                  <p className="font-mono text-[var(--text-muted)]">
                    ···· {linkedProfile.bankAccountLast4}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setLinkedProfile(null);
                    setPaymentError(null);
                  }}
                  className="text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--green)]"
                >
                  Change
                </button>
              </div>
            ) : null}
            {bankCardState === "needs_action" ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-amber-900">Action required</p>
                {tokenError ? <StatusPill kind="error">{tokenError}</StatusPill> : null}
                {paymentLoading || !linkToken ? (
                  <p className="text-xs text-[var(--text-muted)]">Preparing Plaid…</p>
                ) : (
                  <PlaidLinkLaunchButton linkToken={linkToken} onPlaidSuccess={onPlaidSuccess} />
                )}
              </div>
            ) : null}
            {bankCardState === "error" ? (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[var(--badge-red)]">
                  Couldn't link your bank — try again.
                </p>
                {paymentError ? (
                  <p className="text-xs text-[var(--text-secondary)]">{paymentError}</p>
                ) : null}
                {linkToken ? (
                  <PlaidLinkLaunchButton linkToken={linkToken} onPlaidSuccess={onPlaidSuccess} />
                ) : null}
              </div>
            ) : null}
          </PaymentCard>
        </SignedIn>
      </section>
    );
  }
  return Object.assign(
    // eslint-disable-next-line react/display-name
    React.forwardRef(PaymentSectionInner),
    { displayName: "PaymentSection" },
  );
})();

function PaymentCard({
  state,
  title,
  description,
  onSelect,
  children,
}: {
  state: PaymentCardState;
  title: string;
  description: string;
  onSelect: () => void;
  children?: ReactNode;
}) {
  let borderCls: string;
  switch (state) {
    case "ready":
      borderCls = "border border-[var(--green)] ring-1 ring-[var(--green)]/30";
      break;
    case "needs_action":
      borderCls = "border-2 border-dashed border-[var(--green)]";
      break;
    case "error":
      borderCls = "border-2 border-[var(--badge-red)]";
      break;
    default:
      borderCls = "border border-[var(--card-border)] hover:border-[var(--border-hover)]";
  }
  const checked = state !== "unselected";
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`block w-full rounded-2xl bg-white p-4 text-left shadow-cardMd transition-colors ${borderCls}`}
      aria-pressed={checked}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
            checked ? "border-[var(--green)]" : "border-[var(--card-border)]"
          }`}
        >
          {checked ? <span className="h-2 w-2 rounded-full bg-[var(--green)]" /> : null}
        </span>
        <div className="flex-1 space-y-2">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
          <p className="text-xs text-[var(--text-muted)]">{description}</p>
          {children}
        </div>
      </div>
    </button>
  );
}

type OrderSummaryProps = {
  lines: CartLine[];
  subtotal: number;
  customerDeliveryFeeCents: number | null;
  coSurchargeCents: number;
  orderTotalCents: number | null;
  taxCents: number;
  deliveryPaymentMethod: ReturnType<typeof readCheckoutDeliveryPaymentMethod>;
  submitting: boolean;
  disabled: boolean;
  onSubmit: () => void;
};

function OrderSummaryPanel(props: OrderSummaryProps) {
  const {
    lines,
    subtotal,
    customerDeliveryFeeCents,
    coSurchargeCents,
    orderTotalCents,
    taxCents,
    deliveryPaymentMethod,
    submitting,
    disabled,
    onSubmit,
  } = props;
  return (
    <aside className="hidden md:col-span-1 md:block">
      <div className="md:sticky md:top-20 space-y-4">
        <div className="rounded-2xl border border-[var(--card-border)] bg-white p-5 shadow-cardMd">
          <SectionLabel>Order summary</SectionLabel>
          <ul className="mt-3 space-y-3">
            {lines.map((line) => (
              <SummaryLineItem key={line.metrcPackageId} line={line} />
            ))}
          </ul>

          <FeesBreakdown
            subtotal={subtotal}
            customerDeliveryFeeCents={customerDeliveryFeeCents}
            coSurchargeCents={coSurchargeCents}
            orderTotalCents={orderTotalCents}
            taxCents={taxCents}
          />

          {deliveryPaymentMethod === "cash_at_delivery" ? (
            <p className="mt-3 inline-flex items-center rounded-full bg-[var(--surface-sage)] px-3 py-1 text-xs font-medium text-[var(--green-dark)]">
              Paying cash at delivery
            </p>
          ) : null}

          <button
            type="button"
            onClick={onSubmit}
            disabled={disabled}
            className={
              disabled
                ? "mt-5 inline-flex w-full items-center justify-center rounded-lg border border-[var(--card-border)] bg-white px-5 py-2.5 text-sm font-semibold text-[var(--text-muted)]"
                : "mt-5 inline-flex w-full items-center justify-center rounded-lg bg-[var(--green)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
            }
          >
            {submitting ? "Placing order…" : "Place order →"}
          </button>
        </div>
      </div>
    </aside>
  );
}

function SummaryLineItem({ line }: { line: CartLine }) {
  const imgUrl = resolveProductImageUrl(line);
  return (
    <li className="flex items-center gap-3">
      <div
        className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-lg ${imgUrl ? "" : "bg-[var(--surface-sage)]"}`}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt=""
            loading="eager"
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--text-primary)]">{line.productName}</p>
        <p className="text-xs text-[var(--text-muted)]">
          Qty {line.quantity} · {line.quantityUnit}
        </p>
      </div>
      <p className="shrink-0 text-sm font-semibold text-[var(--text-primary)]">
        {formatCentsAsUsd(lineSubtotalCents(line))}
      </p>
    </li>
  );
}

function FeesBreakdown({
  subtotal,
  customerDeliveryFeeCents,
  coSurchargeCents,
  orderTotalCents,
  taxCents,
}: {
  subtotal: number;
  customerDeliveryFeeCents: number | null;
  coSurchargeCents: number;
  orderTotalCents: number | null;
  taxCents: number;
}) {
  return (
    <div className="mt-4 space-y-1.5 border-t border-[var(--card-border)] pt-3 text-sm">
      <FeeRow label="Subtotal" value={formatCentsAsUsd(subtotal)} />
      <FeeRow
        label="Delivery fee"
        value={customerDeliveryFeeCents == null ? "…" : formatCentsAsUsd(customerDeliveryFeeCents)}
      />
      <FeeRow label="CO compliance" value={formatCentsAsUsd(coSurchargeCents)} />
      {/* taxCents > 0 only — keeps the breakdown tight while the field is
       *  forward-compat zero on the API today. */}
      {taxCents > 0 ? <FeeRow label="Tax" value={formatCentsAsUsd(taxCents)} /> : null}
      <div className="flex items-center justify-between border-t border-[var(--card-border)] pt-2">
        <span className="text-sm font-bold text-[var(--text-primary)]">Total</span>
        <span className="text-base font-bold text-[var(--text-primary)]">
          {orderTotalCents == null ? "…" : formatCentsAsUsd(orderTotalCents)}
        </span>
      </div>
    </div>
  );
}

function FeeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[var(--text-secondary)]">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/* ─── Mobile-only summary widgets ───────────────────────────────── */

type MobileSummaryHeaderProps = {
  lines: CartLine[];
  subtotal: number;
  customerDeliveryFeeCents: number | null;
  orderTotalCents: number | null;
  coSurchargeCents: number;
  dispensarySlug: string;
};

function MobileSummaryHeader(props: MobileSummaryHeaderProps) {
  const { lines, subtotal, customerDeliveryFeeCents, orderTotalCents, coSurchargeCents } = props;
  const [open, setOpen] = useState(false);
  const itemCount = lines.reduce((n, l) => n + Math.floor(Number(l.quantity)), 0);
  return (
    <div className="md:hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-2xl border border-[var(--card-border)] bg-white p-3 text-left shadow-cardMd"
        aria-expanded={open}
        aria-controls="mobile-summary-expanded"
      >
        <div className="min-w-0">
          <p className="text-xs text-[var(--text-secondary)]">Order summary</p>
          <p className="text-sm font-semibold text-[var(--text-primary)]">
            {orderTotalCents == null ? "…" : formatCentsAsUsd(orderTotalCents)}{" "}
            <span className="font-normal text-[var(--text-muted)]">
              · {itemCount} item{itemCount === 1 ? "" : "s"} · ETA 35–50 min
            </span>
          </p>
        </div>
        <span aria-hidden className="text-[var(--text-muted)]">
          {open ? "⌃" : "⌄"}
        </span>
      </button>
      {open ? (
        <div
          id="mobile-summary-expanded"
          className="mt-2 rounded-2xl border border-[var(--card-border)] bg-white p-4 shadow-cardMd"
        >
          <ul className="space-y-3">
            {lines.map((line) => (
              <SummaryLineItem key={line.metrcPackageId} line={line} />
            ))}
          </ul>
          <FeesBreakdown
            subtotal={subtotal}
            customerDeliveryFeeCents={customerDeliveryFeeCents}
            coSurchargeCents={coSurchargeCents}
            orderTotalCents={orderTotalCents}
            taxCents={0}
          />
        </div>
      ) : null}
    </div>
  );
}

function MobileStickyBar({
  orderTotalCents,
  submitting,
  disabled,
  onSubmit,
}: {
  orderTotalCents: number | null;
  submitting: boolean;
  disabled: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-16 z-30 border-t border-[var(--card-border)] bg-white px-4 py-3 shadow-elevated md:hidden">
      <div className="mx-auto flex w-full max-w-[1200px] items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-[var(--text-secondary)]">Total</p>
          <p className="text-base font-bold text-[var(--text-primary)]">
            {orderTotalCents == null ? "…" : formatCentsAsUsd(orderTotalCents)}
          </p>
        </div>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled}
          className={
            disabled
              ? "inline-flex items-center justify-center rounded-lg border border-[var(--card-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--text-muted)]"
              : "inline-flex items-center justify-center rounded-lg bg-[var(--green)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--green-dark)]"
          }
        >
          {submitting ? "Placing…" : "Place order →"}
        </button>
      </div>
    </div>
  );
}

/* ─── Tiny shared bits ──────────────────────────────────────────── */

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[.1em] text-[var(--text-secondary)]">
      {children}
    </p>
  );
}

function FieldLabel({
  label,
  className = "",
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function StatusPill({
  kind,
  children,
}: {
  kind: "ok" | "warn" | "error" | "info";
  children: ReactNode;
}) {
  const cls =
    kind === "ok"
      ? "bg-[var(--green-pale)] text-[var(--green-dark)]"
      : kind === "warn"
      ? "bg-amber-50 text-amber-900"
      : kind === "error"
      ? "bg-[var(--status-error-bg)] text-[var(--status-error-text)]"
      : "bg-[var(--page-bg)] text-[var(--text-secondary)]";
  return (
    <p className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
      {children}
    </p>
  );
}

